import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { UserPlus, Trash2, Plus, Globe, KeyRound } from "lucide-react";

export default function Team() {
  const { t, lang, tc } = useI18n();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee" });
  const [countries, setCountries] = useState([]);
  const [newCountry, setNewCountry] = useState("");
  const [savingCountry, setSavingCountry] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  const changeOwnPassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error(lang === "ar" ? "كلمتا المرور غير متطابقتين" : "Passwords do not match");
      return;
    }
    if (pw.new_password.length < 8) {
      toast.error(lang === "ar" ? "كلمة المرور 8 أحرف على الأقل" : "Password must be at least 8 characters");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success(lang === "ar" ? "تم تغيير كلمة المرور" : "Password changed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSavingPw(false); }
  };

  const resetPassword = async (u) => {
    const label = lang === "ar"
      ? `كلمة مرور جديدة لـ ${u.name} (8 أحرف على الأقل):`
      : `New password for ${u.name} (at least 8 characters):`;
    const np = window.prompt(label, "");
    if (np === null) return;
    if (np.trim().length < 8) {
      toast.error(lang === "ar" ? "كلمة المرور قصيرة جداً" : "Password too short");
      return;
    }
    try {
      await api.patch(`/users/${u.id}/password`, { new_password: np.trim() });
      toast.success(lang === "ar"
        ? `تم تعيين كلمة المرور — سلّمها لـ ${u.name}`
        : `Password set — share it with ${u.name}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const loadCountries = () =>
    api.get("/countries").then((r) => setCountries(r.data)).catch(() => {});

  const addCountry = async (e) => {
    e.preventDefault();
    const name = newCountry.trim();
    if (!name) return;
    setSavingCountry(true);
    try {
      const { data } = await api.post("/countries", { name });
      setCountries(data);
      setNewCountry("");
      toast.success(lang === "ar" ? "تمت إضافة الوجهة" : "Destination added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSavingCountry(false); }
  };

  const removeCountry = async (name) => {
    const msg = lang === "ar"
      ? `حذف "${name}" من قائمة وجهات الدراسة؟`
      : `Remove "${name}" from the list of study destinations?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(`/countries/${encodeURIComponent(name)}`);
      setCountries(data);
      toast.success(lang === "ar" ? "تم الحذف" : "Removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); loadCountries(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      setForm({ name: "", email: "", password: "", role: "employee" });
      toast.success(t("addEmployee"));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1000px] mx-auto">
        <div className="mb-6">
          <div className="eyebrow">{t("appName")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#0B192C] mt-1">
            {t("team")}
          </h1>
        </div>

        <Card className="p-5 border border-slate-200 shadow-none mb-6">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end" data-testid="new-user-form">
            <div>
              <Label className="text-xs">{t("yourName")}</Label>
              <Input value={form.name} required
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="new-user-name" />
            </div>
            <div>
              <Label className="text-xs">{t("email")}</Label>
              <Input type="email" value={form.email} required
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     data-testid="new-user-email" />
            </div>
            <div>
              <Label className="text-xs">{t("password")}</Label>
              <Input type="password" value={form.password} required minLength={6}
                     onChange={(e) => setForm({ ...form, password: e.target.value })}
                     data-testid="new-user-password" />
            </div>
            <div>
              <Label className="text-xs">{t("role")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">{t("employee")}</SelectItem>
                  <SelectItem value="admin">{t("admin")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="bg-[#7A1B28] hover:bg-[#601520] text-white"
                    data-testid="new-user-save">
              <UserPlus size={14} className="me-2" /> {t("createAccount")}
            </Button>
          </form>
        </Card>

        <Card className="border border-slate-200 shadow-none overflow-x-auto" data-testid="users-list">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">{t("name")}</th>
                <th className="px-4 py-3">{t("email")}</th>
                <th className="px-4 py-3">{t("role")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100">
                  <td className="px-4 py-3 font-medium text-[#0B192C]">{u.name}</td>
                  <td className="px-4 py-3 text-slate-700">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-1 rounded-md border border-slate-200 bg-slate-50 uppercase tracking-wider">
                      {u.role === "admin" ? t("admin") : t("employee")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-end">
                    <div className="inline-flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => resetPassword(u)}
                              title={lang === "ar" ? "تعيين كلمة مرور جديدة" : "Set a new password"}
                              data-testid={`user-reset-pw-${u.id}`}>
                        <KeyRound size={14} />
                      </Button>
                      {u.id !== user?.id && (
                        <Button size="sm" variant="outline" onClick={() => remove(u.id)}
                                data-testid={`user-delete-${u.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card className="p-5 border border-slate-200 shadow-none mb-6" data-testid="my-password-card">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound size={18} className="text-[#7A1B28]" />
            <h2 className="font-display text-lg font-semibold text-[#0B192C]">
              {lang === "ar" ? "كلمة المرور الخاصة بي" : "My password"}
            </h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            {lang === "ar"
              ? "غيّرها من وقت لآخر، ولا تشاركها مع أحد."
              : "Change it from time to time, and keep it to yourself."}
          </p>
          <form onSubmit={changeOwnPassword} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs">{lang === "ar" ? "كلمة المرور الحالية" : "Current password"}</Label>
              <Input type="password" required value={pw.current_password}
                     onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                     data-testid="current-password" />
            </div>
            <div>
              <Label className="text-xs">{lang === "ar" ? "كلمة المرور الجديدة" : "New password"}</Label>
              <Input type="password" required value={pw.new_password}
                     onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                     data-testid="new-password" />
            </div>
            <div>
              <Label className="text-xs">{lang === "ar" ? "تأكيد الجديدة" : "Confirm new"}</Label>
              <Input type="password" required value={pw.confirm}
                     onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                     data-testid="confirm-password" />
            </div>
            <Button type="submit" disabled={savingPw}
                    className="bg-[#7A1B28] hover:bg-[#601520] text-white"
                    data-testid="change-password-btn">
              {savingPw
                ? (lang === "ar" ? "جارِ الحفظ…" : "Saving…")
                : (lang === "ar" ? "تغيير" : "Change")}
            </Button>
          </form>
        </Card>

        <Card className="p-5 border border-slate-200 shadow-none" data-testid="countries-card">
          <div className="flex items-center gap-2 mb-1">
            <Globe size={18} className="text-[#7A1B28]" />
            <h2 className="font-display text-lg font-semibold text-[#0B192C]">
              {lang === "ar" ? "وجهات الدراسة" : "Study destinations"}
            </h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            {lang === "ar"
              ? "الدول التي تظهر عند إضافة طالب أو تعديل ملفه."
              : "The countries offered when adding or editing a student."}
          </p>

          <form onSubmit={addCountry} className="flex gap-2 mb-4 flex-wrap">
            <Input value={newCountry}
                   onChange={(e) => setNewCountry(e.target.value)}
                   placeholder={lang === "ar" ? "اسم الدولة بالإنجليزية، مثال: Hungary" : "Country name, e.g. Hungary"}
                   className="max-w-xs"
                   data-testid="new-country-input" />
            <Button type="submit" disabled={savingCountry || !newCountry.trim()}
                    className="bg-[#7A1B28] hover:bg-[#601520] text-white"
                    data-testid="add-country-btn">
              <Plus size={16} className="me-2" />
              {lang === "ar" ? "إضافة" : "Add"}
            </Button>
          </form>

          <div className="flex flex-wrap gap-2" data-testid="countries-list">
            {countries.map((c) => (
              <span key={c}
                    className="inline-flex items-center gap-2 ps-3 pe-2 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-sm">
                {tc ? tc(c) : c}
                <button type="button" onClick={() => removeCountry(c)}
                        className="text-slate-400 hover:text-red-600"
                        aria-label={`Remove ${c}`}
                        data-testid={`remove-country-${c}`}>
                  <Trash2 size={13} />
                </button>
              </span>
            ))}
            {countries.length === 0 && (
              <span className="text-sm text-slate-500">
                {lang === "ar" ? "لا توجد وجهات بعد." : "No destinations yet."}
              </span>
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
}
